import { dehydrate, QueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { MdSearchOff } from 'react-icons/md';

import FeaturedPost from '@/components/Core/FeaturedPost/FeaturedPost';
import PostCard from '@/components/Core/PostCard/PostCard';
import MainLayout from '@/components/Layout/Main';
import { CustomMeta } from '@/components/Meta/CustomMeta';
import Button from '@/components/Primitive/Button/Button';
import Spinner from '@/components/Primitive/Spinner/Spinner';
import usePagination, {
  makeQuery as makePaginatedQuery,
} from '@/hooks/use-pagination';
import useQuery, { makeQuery } from '@/hooks/use-query';
import {
  createNewsPageQuery,
  mapToNewsPageContent,
} from '@/queries/globals/news';
import {
  createNewsCategoriesQuery,
  mapToNewsCategories,
} from '@/queries/news/categories';
import {
  createPaginatedNewsListQuery,
  mapToPaginatedNewsList,
} from '@/queries/news/list';

const newsListQuery = createPaginatedNewsListQuery();

const newsCategoryQuery = createNewsCategoriesQuery();
const newsCategoryQueryVars = {};

const pageQuery = createNewsPageQuery();
const pageQueryVars = {};

const Page = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const { data: pageData } = useQuery(
    pageQuery,
    pageQueryVars,
    mapToNewsPageContent
  );

  const newsListQueryVars = {
    ...(selectedCategory && { category: selectedCategory }),
  };

  const {
    data: newsData,
    isFetchingNextPage,
    loading,
    hasMore,
    fetchMore,
  } = usePagination(newsListQuery, newsListQueryVars, {
    formatFn: mapToPaginatedNewsList,
    limit: 6,
  });

  const { data: categoriesData } = useQuery(
    newsCategoryQuery,
    newsCategoryQueryVars,
    mapToNewsCategories
  );

  const handleCategorySelect = (value: string) => {
    if (value === selectedCategory) {
      setSelectedCategory(null);
      return;
    }

    setSelectedCategory(value);
  };

  const categories = categoriesData
    ? [...categoriesData, { id: '', name: 'All' }]
    : [];

  return (
    <MainLayout withNavSpacer>
      <CustomMeta {...pageData?.seo} />
      <div className="pb-page overflow-x-hidden">
        <div className="container-md space-y-6 py-4 pb-8 md:space-y-16 md:py-14">
          {pageData?.featuredPost && (
            <FeaturedPost
              className="pb-8 md:pb-0 "
              {...pageData.featuredPost}
              thumbnail={pageData.featuredPost.thumbnailLarge}
            />
          )}
          <div className="flex flex-col items-center justify-center space-y-6 md:space-y-8">
            <div className="flex flex-wrap gap-2 self-start font-light text-cream md:self-end">
              Filter by:
              <div className="flex flex-wrap gap-2">
                {categories?.map((c, i) => {
                  return (
                    <>
                      <button
                        className={clsx(
                          'font-medium text-cream hover:underline',
                          selectedCategory === c.id && 'text-offWhite underline'
                        )}
                        key={c.id}
                        onClick={() => handleCategorySelect(c?.id)}
                      >
                        {c.name}
                      </button>
                      {i !== categories.length - 1 && (
                        <span className="font-medium text-cream">\</span>
                      )}
                    </>
                  );
                })}
              </div>
            </div>
            <div className="grid w-full gap-3 sm:grid-cols-2 md:grid-cols-3">
              {newsData?.docs?.map((news) => (
                <PostCard
                  {...news}
                  category={news.category?.name}
                  key={news.id}
                />
              ))}
            </div>
            {!isFetchingNextPage &&
              !loading &&
              !hasMore &&
              newsData?.docs.length === 0 && (
                <p className="flex flex-col items-center justify-center text-cream">
                  <MdSearchOff className="h-14 w-14" />
                  Could not find any posts
                </p>
              )}
            {loading && (
              <div className="flex h-[350px] items-center justify-center">
                <Spinner className="h-28 w-28 text-cream" />
              </div>
            )}
            {hasMore && (
              <Button
                loading={isFetchingNextPage}
                className="mt-8 uppercase"
                onClick={() => fetchMore()}
              >
                View More
              </Button>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Page;

export const getStaticProps = async () => {
  const queryClient = new QueryClient();

  await queryClient.prefetchQuery(
    ['query', pageQuery, pageQueryVars],
    async () => {
      return makeQuery(pageQuery, pageQueryVars, mapToNewsPageContent);
    }
  );

  await queryClient.prefetchQuery(
    ['query', newsCategoryQuery, newsCategoryQueryVars],
    async () => {
      return makeQuery(
        newsCategoryQuery,
        newsCategoryQueryVars,
        mapToNewsCategories
      );
    }
  );

  await queryClient.prefetchInfiniteQuery(
    ['pagination', newsListQuery, {}],
    async () => {
      return makePaginatedQuery(
        newsListQuery,
        {},
        1,
        6,
        mapToPaginatedNewsList
      );
    }
  );

  queryClient.setQueryData(['pagination', newsListQuery, {}], (data) => {
    return Object.assign({}, data, { pageParams: [] });
  });

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
    revalidate: 60,
  };
};
